import LanguageSelector from "../common/LanguageSelector";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import "../../assets/css/auth.css";
import { BlackLogo } from "../common/BlackLogo";
import axios from "axios";
import { setAlert } from "../../actions/alert";
import { useSelector, useDispatch } from "react-redux";
import Alert from "../common/Alert";

export default function ForgotPassword() {
  useTranslation();
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {}, []);

  const validateEmail = () => {
    let emailText = email || "";
    let errs = {};
    let formIsValid = true;
    if (!emailText) { formIsValid = false; errs["email"] = "Please enter your email address."; }
    if (emailText) {
      let pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
      if (!pattern.test(emailText)) { formIsValid = false; errs["email"] = "Please enter a valid email address."; }
    }
    setErrors(errs);
    return formIsValid;
  };

  const sendEmail = async () => {
    if (loading) return;
    if (!validateEmail()) return;
    setLoading(true);
    try {
      const config = { headers: { Accept: "application/json", "Content-Type": "application/json" } };
      const body = JSON.stringify({ email });
      const res = await axios.post("/api/profile/forgot_password", body, config);
      if (res.status === 200 && res.data.user === true) {
        setSubmitted(true);
        setEmail("");
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if (status === 429) {
          setErrors({ email: tr("Too many attempts. Please wait a moment and try again.") });
        } else if (status >= 500) {
          setErrors({ email: tr("Something went wrong on our end. Please try again in a moment.") });
        } else {
          const msg = error.response.data?.errors?.[0]?.msg;
          setErrors({ email: msg || tr("An error occurred. Please try again.") });
        }
      } else {
        setErrors({ email: tr("Unable to connect. Please check your internet connection.") });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      {/* ── Left Panel ── */}
      <div className="auth-left">
        <div className="auth-logo">
          <BlackLogo />
        </div>

        <div className="auth-hero">
          <div className="auth-hero-badge">
            <span className="auth-hero-badge-dot"></span>{tr("Account Recovery")} </div>
          <h1>{tr("We'll get you back")} <span>{tr("in.")}</span></h1>
          <p>{tr("No worries — it happens to everyone. Enter your email and we'll send you a secure reset link within minutes.")}</p>

          <div className="auth-features">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Secure, time-limited reset link")}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Email delivered in under 60 seconds")}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Your account data stays safe")}</span>
            </div>
          </div>
        </div>

        <div className="auth-stats">
          <div className="auth-stat-item">
            <div className="auth-stat-value">{tr("< 1min")}</div>
            <div className="auth-stat-label">{tr("Email delivery")}</div>
          </div>
          <div className="auth-stat-item">
            <div className="auth-stat-value">SSL</div>
            <div className="auth-stat-label">{tr("Encrypted")}</div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="auth-right">
        <div className="auth-card">
          <LanguageSelector />

          <NavLink to="/login" className="auth-back-link">
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>{tr("Back to sign in")} </NavLink>

          <div className="auth-card-header">
            <h2>{tr("Reset your password")}</h2>
            <p>{tr("Enter the email address associated with your account and we'll send you a reset link.")}</p>
          </div>

          {submitted && (
            <div className="auth-success-box">
              <div className="auth-success-icon">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div className="auth-success-text">
                <h4>{tr("Reset link sent!")}</h4>
                <p>{tr("Check your inbox (and spam folder) for the password reset email.")}</p>
              </div>
            </div>
          )}

          <div className="auth-alert-wrap"><Alert alerts="" /></div>

          <form className="auth-form" onSubmit={(e) => { e.preventDefault(); sendEmail(); }} noValidate>
            <div className="auth-field">
              <label className="auth-label" htmlFor="forgot-email">{tr("Email address")}</label>
              <input
                className={`auth-input${errors.email ? " is-error" : ""}`}
                type="email"
                id="forgot-email"
                name="email"
                placeholder={tr("you@example.com")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoCorrect="off"
                autoCapitalize="off"
                autoComplete="email"
                spellCheck="false"
              />
              {errors.email && <span className="auth-error-msg">{tr(errors.email)}</span>}
            </div>

            <button type="button" className="auth-submit-btn" onClick={sendEmail} disabled={loading}>
              {loading ? tr("Sending…") : tr("Send reset link")}
            </button>
          </form>

          <p className="auth-footer-link">{tr("Don't have an account?")} <NavLink to="/">{tr("Sign up for free")}</NavLink>
          </p>
        </div>
      </div>
    </div>
  );
}
